import { convertToKey } from '@gonfalon/strings/convertToKey';
import { z } from 'zod';

import { getModelFamilyFromModelName } from '#components/AiConfigs/AiConfigDetails/ModelIcon';
import type { AiConfigParametersType } from '#components/AiConfigs/types';

import type { AiConfigModelForm } from '../AiConfigModelConfiguration';
import type { AiConfigModel, AIConfigModelResponse } from '../types';

import { ai21LabsJsonSchema, retrieveAi21LabsParameters } from './Ai21Labs';
import { anthropicJsonSchema, retrieveAnthropicParameters } from './Anthropic';
import { azureJsonSchema, retrieveAzureParameters } from './Azure';
import type { BedrockVariant } from './Bedrock';
import { bedrockVariantSchemas, retrieveBedrockParametersByVariant } from './Bedrock';
import { cohereJsonSchema, retrieveCohereParameters } from './Cohere';
import { cortexJsonSchema, retrieveCortexParameters } from './Cortex';
import { cursorJsonSchema, retrieveCursorParameters } from './Cursor';
import { databricksJsonSchema, retrieveDatabricksParameters } from './Databricks';
import { deepSeekJsonSchema, retrieveDeepSeekParameters } from './DeepSeek';
import { fireworksAIJsonSchema, retrieveFireworksAIParameters } from './FireworksAI';
import { geminiJsonSchema, retrieveGeminiParameters } from './Gemini';
import { metaJsonSchema, retrieveMetaParameters } from './Meta';
import { mistralJsonSchema, retrieveMistralParameters } from './Mistral';
import { openAiJsonSchema, retrieveOpenAiParameters } from './OpenAi';
import { perplexityJsonSchema, retrievePerplexityParameters } from './Perplexity';
import { retrieveWatsonParameters } from './Watson';

// If updating this list, also update the providerNameMapping in internal/ai/ai_configs/ai_evaluator_proxy/provider_key_middleware.go
export const modelProviders = [
  'Anthropic',
  'OpenAI',
  'Bedrock',
  'Azure',
  'Gemini',
  'AI21 Labs',
  'Cohere',
  'Cortex',
  'Cursor',
  'Databricks',
  'DeepSeek',
  'Fireworks AI',
  'IBM Watson',
  'Meta',
  'Mistral',
  'Perplexity',
  'TypeSafe',
  'Vertex',
] as const;

export type ModelProvider = (typeof modelProviders)[number];

const modelProviderLookup = new Set<string>(modelProviders);

/** True when `value` is a known {@link modelProviders} entry. */
export function isModelProvider(value: string | null | undefined): value is ModelProvider {
  return value !== null && value !== undefined && value !== '' && modelProviderLookup.has(value);
}

export function getBaseProviderName(value: string | null | undefined): string {
  return value?.split(':')[0] ?? '';
}

/**
 * Maps API/UI provider strings to a {@link ModelProvider} for base-provider UI
 * (including composite Bedrock variants such as `Bedrock:Anthropic`).
 */
export function toBaseModelProvider(value: string | null | undefined): ModelProvider | null {
  const base = getBaseProviderName(value);
  return isModelProvider(base) ? base : null;
}

export function getProviderFromModelConfigKey(modelConfigKey: string | null | undefined): ModelProvider | null {
  if (!modelConfigKey?.includes('.')) {
    return null;
  }
  return toBaseModelProvider(modelConfigKey.split('.')[0]);
}

const customOnlyProviders: ReadonlySet<ModelProvider> = new Set(['Azure']);

export function isCustomOnlyProvider(provider: ModelProvider | undefined): boolean {
  return provider !== undefined && customOnlyProviders.has(provider);
}

export function getFilteredModelProviders<const T extends readonly ModelProvider[]>(
  excluded: T,
): Array<Exclude<ModelProvider, T[number]>> {
  const excludedSet = new Set(excluded);
  return modelProviders.filter((provider) => !excludedSet.has(provider)) as Array<Exclude<ModelProvider, T[number]>>;
}

type ParamTranslationResult = {
  requiresTranslation: boolean;
  settledParams: AiConfigParametersType;
  droppedParams: AiConfigParametersType;
};

const MODEL_CONFIG_KEY_SUFFIX_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const MODEL_CONFIG_KEY_SUFFIX_LENGTH = 6;

function formatModelConfigKeyDate(date: Date = new Date()): string {
  return date.toISOString().slice(0, 10).replaceAll('-', '');
}

function generateModelConfigKeySuffix(length = MODEL_CONFIG_KEY_SUFFIX_LENGTH): string {
  return Array.from({ length }, () => {
    const index = Math.floor(Math.random() * MODEL_CONFIG_KEY_SUFFIX_ALPHABET.length);
    return MODEL_CONFIG_KEY_SUFFIX_ALPHABET[index];
  }).join('');
}

function buildCustomModelConfigKey(modelId: string): string {
  return `${convertToKey(modelId)}-${formatModelConfigKeyDate()}-${generateModelConfigKeySuffix()}`;
}

const baseModelSchema = z.object({
  id: z.string().trim().min(1, 'Model ID is required'),
  name: z.string().trim().min(1, 'Name is required'),
  // Not restricted to `modelProviders`: a custom provider's own name is a valid, arbitrary
  // string here (see `isCustomProvider`/`customProviderValue`), not one of the fixed built-ins.
  provider: z.string().min(1),
  params: z.any(),
  customParams: z.string().optional(),
});

export const imageModels = ['dall-e'];

/** Present in some provider JSON schemas but not user-editable; Gonfalon injects `model.parameters.tools` from the Tools flow (see internal/ai/ai_configs/internal/domain/variation.go). */
const RESERVED_MODEL_PARAMETER_KEYS: ReadonlySet<string> = new Set(['tools']);

// Singleton
let aiConfigModelServiceInstance: ReturnType<typeof aiConfigModelService> | undefined;

export const aiConfigModelService = () => {
  const getParametersForProvider = (provider: ModelProvider | BedrockVariant | undefined): string[] => {
    if (!provider) {
      return [];
    }

    let keys: string[];
    switch (provider) {
      case 'OpenAI':
        keys = retrieveOpenAiParameters();
        break;
      case 'Anthropic':
        keys = retrieveAnthropicParameters();
        break;
      case 'Bedrock:Anthropic':
      case 'Bedrock:Mistral':
      case 'Bedrock:Titan':
      case 'Bedrock:Meta':
      case 'Bedrock:Stability':
      case 'Bedrock:Cohere':
      case 'Bedrock:Nova':
        keys = retrieveBedrockParametersByVariant(provider);
        break;
      case 'Azure':
        keys = retrieveAzureParameters();
        break;
      case 'Meta':
        keys = retrieveMetaParameters();
        break;
      case 'Gemini':
        keys = retrieveGeminiParameters();
        break;
      case 'Cohere':
        keys = retrieveCohereParameters();
        break;
      case 'Mistral':
        keys = retrieveMistralParameters();
        break;
      case 'DeepSeek':
        keys = retrieveDeepSeekParameters();
        break;
      case 'AI21 Labs':
        keys = retrieveAi21LabsParameters();
        break;
      case 'Vertex':
        keys = [];
        break;
      case 'IBM Watson':
        keys = retrieveWatsonParameters();
        break;
      case 'Cortex':
        keys = retrieveCortexParameters();
        break;
      case 'Cursor':
        keys = retrieveCursorParameters();
        break;
      case 'Perplexity':
        keys = retrievePerplexityParameters();
        break;
      case 'TypeSafe':
        keys = [];
        break;
      case 'Databricks':
        keys = retrieveDatabricksParameters();
        break;
      case 'Fireworks AI':
        keys = retrieveFireworksAIParameters();
        break;
      default:
        keys = [];
    }

    return keys.filter((key) => !RESERVED_MODEL_PARAMETER_KEYS.has(key));
  };

  function retrieveParameter(
    provider: ModelProvider | BedrockVariant,
    key: string,
  ): {
    type: Readonly<string | string[]>;
    default?: string | number | boolean | null | undefined;
  } {
    switch (provider) {
      case 'OpenAI':
        return openAiJsonSchema.properties[key as keyof typeof openAiJsonSchema.properties];
      case 'Anthropic':
        return anthropicJsonSchema.properties[key as keyof typeof anthropicJsonSchema.properties];
      case 'Bedrock:Anthropic':
      case 'Bedrock:Mistral':
      case 'Bedrock:Titan':
      case 'Bedrock:Meta':
      case 'Bedrock:Stability':
      case 'Bedrock:Cohere':
        const providerSubSchema = bedrockVariantSchemas[provider];
        if (key in providerSubSchema.properties) {
          return providerSubSchema.properties[key as keyof typeof providerSubSchema.properties];
        }

        return { type: '' };
      case 'Azure':
        return azureJsonSchema.properties[key as keyof typeof azureJsonSchema.properties];
      case 'Meta':
        return metaJsonSchema.properties[key as keyof typeof metaJsonSchema.properties];
      case 'Gemini':
        return geminiJsonSchema.properties[key as keyof typeof geminiJsonSchema.properties];
      case 'Cohere':
        return cohereJsonSchema.properties[key as keyof typeof cohereJsonSchema.properties];
      case 'Mistral':
        return mistralJsonSchema.properties[key as keyof typeof mistralJsonSchema.properties];
      case 'DeepSeek':
        return deepSeekJsonSchema.properties[key as keyof typeof deepSeekJsonSchema.properties];
      case 'AI21 Labs':
        return ai21LabsJsonSchema.properties[key as keyof typeof ai21LabsJsonSchema.properties];
      case 'Vertex':
        return { type: '' };
      case 'Cortex':
        return cortexJsonSchema.properties[key as keyof typeof cortexJsonSchema.properties];
      case 'Cursor':
        return cursorJsonSchema.properties[key as keyof typeof cursorJsonSchema.properties];
      case 'Perplexity':
        return perplexityJsonSchema.properties[key as keyof typeof perplexityJsonSchema.properties];
      case 'TypeSafe':
        return { type: '' };
      case 'Databricks':
        return databricksJsonSchema.properties[key as keyof typeof databricksJsonSchema.properties];
      case 'Fireworks AI':
        return fireworksAIJsonSchema.properties[key as keyof typeof fireworksAIJsonSchema.properties];
      default:
        return { type: '' };
    }
  }

  function typeMatchesSchema(
    type: string,
    value: string | number | boolean | null,
  ): [boolean, string | number | boolean | null] {
    // Return a type of [boolean, value] where boolean is whether or not the validation was a success,
    // and the value is the parsed value that was validated against the rules (ie. "1" -> 1, "true" -> true)
    switch (type) {
      case 'string':
        return [typeof value === 'string' && value.length > 0, value];
      case 'integer':
        if (value === undefined || value === '') {
          return [false, value];
        }
        return [!isNaN(Number(value)) && Number.isInteger(Number(value)), Number(value)];
      case 'number':
        return [!isNaN(Number(value)), value];
      case 'boolean':
        return [typeof value === 'boolean' || value === 'true' || value === 'false', Boolean(value)];
      case 'object':
        if (typeof value !== 'string') {
          return [false, value];
        }
        try {
          JSON.parse(value);
          return [true, value];
        } catch {
          return [false, value];
        }
      case 'array':
        if (typeof value !== 'string') {
          return [false, value];
        }
        try {
          const parsed = JSON.parse(value);
          return [Array.isArray(parsed), parsed];
        } catch {
          return [false, value];
        }
      case 'null':
        return [value === null || value === undefined, null];
      case '':
        // No schema is known for this provider/param (e.g. a custom provider, or a provider
        // like Vertex with no defined param schema) — nothing to validate against, so let it through.
        return [true, value];
      default:
        return [false, value];
    }
  }

  function validateParam(
    paramName: string,
    schema: ReturnType<typeof retrieveParameter>,
    value: string | number | boolean | null,
  ) {
    let types = schema.type;
    if (typeof types === 'string') {
      types = [types];
    }
    // We want to find the first match and continue returning that so that we can
    // also capture the parsed value (e.g. "1" -> 1). This saves us from doing another pass later to get
    // the transformed/parsed values
    const [paramsSuccess, parsedValue] = types.reduce(
      (c, n) => {
        if (c[0]) {
          return c;
        }

        const [success, val] = typeMatchesSchema(n, value);
        // Already found a match, carry this through
        if (success) {
          return [success, val];
        } else {
          return c;
        }
      },
      [false, null] as [boolean, string | number | boolean | null],
    );

    if (paramsSuccess) {
      return { error: null, value: parsedValue };
    } else {
      return { error: { [paramName]: `Must provide a value of type ${types.join(' or ')}` }, value };
    }
  }
  // Handles validation of the base model schema as well as the custom parameters
  // for a given provider. Returns an object with the following shape:
  // {
  //   hasErrors: boolean;
  //   errors: Record<string, string>; // Field errors, e.g. { "custom": "Invalid JSON" }
  //   parameters: Record<string, string | number | boolean | null>; // Parsed param, e.g. "1" -> 1
  // }
  const validateParams = (
    providerId: ModelProvider | BedrockVariant,
    params: AiConfigModelForm,
  ): {
    hasErrors: boolean;
    errors: Record<string, string>;
    parameters: Record<string, string | number | boolean | null>;
  } => {
    let errors: Record<string, string> = {};
    const values: Record<string, string | number | boolean | null> = {};
    // Parse the base "model" schema by itself with .any() for the parameters, those will be parsed if this evaluates successfully
    const baseResult = baseModelSchema.safeParse(params);
    if (!baseResult.success) {
      // Flatten the errors to a single level
      const fieldErrors = baseResult.error.flatten().fieldErrors;
      const fieldErrorKeys = Object.keys(fieldErrors);
      fieldErrorKeys.forEach((key) => {
        if (key in fieldErrors) {
          // TS is silly here; we know the keys are keys of this object
          errors[key] = fieldErrors[key as keyof typeof fieldErrors]?.[0] ?? '';
        }
      });
    }

    if (params.params) {
      const keys = Object.keys(params.params);
      for (const entry of keys) {
        const parameterSchema = retrieveParameter(providerId, entry);
        const paramValue = params.params[entry];
        // Validate the parameter against the attributes of the JSON schema, by parameter name
        const { error, value } = validateParam(entry, parameterSchema, paramValue);
        // If we have errors, add them to the overall errors object
        if (error) {
          errors = { ...errors, ...error };
        } else {
          // Otherwise, add the parsed value to the overall values object
          values[entry] = value;
        }
      }
    }

    // Validate the custom parameters, just a simple JSON parse for now as we don't
    // want to restrict any custom data being passed here
    if (params.customParams) {
      try {
        JSON.parse(params.customParams);
      } catch {
        errors.custom = 'This must be valid JSON';
      }
    }

    return { hasErrors: Object.keys(errors).length > 0, errors, parameters: values };
  };

  const inferBedrockVariantFromModelName = (modelName: string): BedrockVariant | 'Bedrock' => {
    if (modelName.includes('anthropic.')) {
      return 'Bedrock:Anthropic';
    }

    if (modelName.includes('mistral.')) {
      return 'Bedrock:Mistral';
    }

    if (modelName.includes('titan.')) {
      return 'Bedrock:Titan';
    }

    if (modelName.includes('meta.')) {
      return 'Bedrock:Meta';
    }

    if (modelName.includes('stability.')) {
      return 'Bedrock:Stability';
    }

    if (modelName.includes('cohere.')) {
      return 'Bedrock:Cohere';
    }

    if (modelName.includes('nova.')) {
      return 'Bedrock:Nova';
    }

    return 'Bedrock';
  };

  const inferProviderFromModelName = (modelName: string): ModelProvider | BedrockVariant => {
    const modelFamily = getModelFamilyFromModelName(modelName);

    if (modelFamily === 'Bedrock') {
      return inferBedrockVariantFromModelName(modelName);
    }

    return modelFamily || 'OpenAI';
  };

  const translateParamsBetweenFamilies = (
    newModelSelection: { provider?: AiConfigModel['provider']; modelName: AiConfigModel['modelName'] },
    oldModelSelection: { provider?: AiConfigModel['provider']; modelName: AiConfigModel['modelName'] },
    params: AiConfigParametersType,
  ): ParamTranslationResult => {
    if (
      newModelSelection.provider === oldModelSelection.provider &&
      newModelSelection.provider !== undefined &&
      oldModelSelection.provider !== undefined
    ) {
      return { requiresTranslation: false, settledParams: params, droppedParams: {} };
    }

    const newModelFamily = getModelFamilyFromModelName(newModelSelection.modelName, newModelSelection.provider);
    const oldModelFamily = getModelFamilyFromModelName(oldModelSelection.modelName, oldModelSelection.provider);

    if (newModelFamily === oldModelFamily || newModelFamily === '') {
      return { requiresTranslation: false, settledParams: params, droppedParams: {} };
    }

    const newModelProviderParams = getParametersForProvider(newModelFamily);
    const settledParams = newModelProviderParams.reduce<Record<string, string | number | boolean | null>>(
      (acc, param) => {
        if (params[param] !== undefined) {
          return { ...acc, [param]: params[param] };
        }

        return { ...acc };
      },
      {},
    );

    const droppedParams = Object.keys(params).reduce<Record<string, string | number | boolean | null | undefined>>(
      (acc, next) => {
        if (settledParams[next] === undefined) {
          return { ...acc, [next]: params[next] };
        }

        return acc;
      },
      {},
    );

    return { requiresTranslation: Object.keys(droppedParams).length > 0, settledParams, droppedParams };
  };

  return {
    retrieveParameter,
    getParametersForProvider,
    validateParams,
    typeMatchesSchema,
    translateParamsBetweenFamilies,
    inferProviderFromModelName,
    inferBedrockVariantFromModelName,

    toMutableAiConfigModel: (model: AIConfigModelResponse): AiConfigModel => ({
      ...model,
      provider: model.provider as ModelProvider | BedrockVariant,
      params: model.params || {},
    }),

    toPostableAiConfigModel: (
      model: AiConfigModelForm,
      isCustomProvider: boolean,
      validatedInputTokens: number,
      validatedOutputTokens: number,
      validatedCachedInputTokens: number,
      customProviderValue?: string,
      isCostMetricsEnabled: boolean = false,
      isCachedInputTokenCostFieldEnabled: boolean = false,
    ): AIConfigModelResponse => {
      const provider = model.subProvider ? `${model.provider}:${model.subProvider}` : model.provider;
      return {
        name: model.name,
        modelName: model.id,
        global: false,
        key: buildCustomModelConfigKey(model.id),
        provider: isCustomProvider ? customProviderValue : provider,
        // Not force-cleared for custom providers: the create flow already clears `params` when
        // the user picks "Custom" (there's no UI to add params for one), but on edit an existing
        // custom-provider model can have params from before, and those must be preserved rather
        // than wiped out by an unrelated field edit.
        params: model.params as Record<string, never>,
        customParams: JSON.parse(model.customParams ?? '{}') as Record<string, never>,
        costPerInputToken: isCostMetricsEnabled && validatedInputTokens ? validatedInputTokens : 0,
        costPerOutputToken: isCostMetricsEnabled && validatedOutputTokens ? validatedOutputTokens : 0,
        costPerCachedInputToken: isCachedInputTokenCostFieldEnabled
          ? isCostMetricsEnabled && validatedCachedInputTokens
            ? validatedCachedInputTokens
            : 0
          : undefined,
      };
    },
  };
};

export const useAiConfigModelService = (): ReturnType<typeof aiConfigModelService> => {
  if (!aiConfigModelServiceInstance) {
    aiConfigModelServiceInstance = aiConfigModelService();
  }

  return aiConfigModelServiceInstance;
};

/**
 * Providers the evaluations API can report, mapped from the lowercase names it
 * returns to their canonical casing. Keep in sync with `validProviders` in
 * internal/ai/ai_configs/ai_evaluator_proxy/provider_key_middleware.go — a
 * provider missing here is silently dropped, which hides it from the Playground
 * model picker and marks its models unrunnable.
 */
const canonicalProviderNames: Readonly<Record<string, ModelProvider>> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  bedrock: 'Bedrock',
};

export const providersFromLowercase = (input: string[]): ModelProvider[] =>
  input
    .map((provider) => canonicalProviderNames[provider.toLowerCase()])
    .filter((provider): provider is ModelProvider => Boolean(provider));
